import React, { useState } from 'react';
import { Button, Icon } from '../../components/ui';
import { registerPasskey } from './passkeyApi';

/**
 * Post-login passkey enrollment prompt.
 *
 * WHY THIS EXISTS: offering passkeys only in profile settings converts at ~10%.
 * Prompting immediately after a successful login is the highest-converting
 * moment (eBay: +102% adoption, 75% of all passkeys created via this path), so
 * this is shown once sign-in succeeds, before the user reaches the dashboard.
 *
 * Deliberately NOT a hard gate: the research is emphatic that forcing immediate
 * enrollment is the worst way to roll passkeys out, and pestering someone who
 * declined drives abandonment of the whole login flow. So "not now" is a
 * first-class, frictionless exit, and the caller remembers the dismissal.
 */
export const PasskeyEnrollPrompt: React.FC<{
  onDone: () => void;
  onSkip: () => void;
}> = ({ onDone, onSkip }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const enroll = async () => {
    setBusy(true);
    setError('');
    try {
      await registerPasskey(deviceLabel());
      onDone();
    } catch (err: any) {
      // Cancelling the OS sheet is a normal "no" — treat it as a skip and let
      // the user through, rather than parking them on this card with no way
      // forward. (They already authenticated; this screen is an offer, not a gate.)
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        onSkip();
        return;
      }
      if (err?.name === 'InvalidStateError') {
        // Already registered on this device — nothing to do, let them through.
        onDone();
        return;
      }
      // ConstraintError: the authenticator can't store a discoverable
      // credential (a full security key). Nothing the user can fix here, so
      // don't strand them — say so briefly and move on.
      if (err?.name === 'ConstraintError') {
        onSkip();
        return;
      }
      setError('לא הצלחנו להוסיף מפתח גישה. אפשר לנסות שוב מאוחר יותר מהפרופיל.');
      setBusy(false);
    }
  };

  return (
    <div className="text-center">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
        <Icon name="key" size={28} className="text-primary" />
      </div>

      <h2 className="text-xl font-bold text-slate-900 mb-2">להיכנס בפעם הבאה בלי סיסמה?</h2>
      {/* One plain benefit sentence — jargon like "WebAuthn"/"FIDO2" measurably
          lowers completion, so it is kept out of the user-facing copy. */}
      <p className="text-sm text-slate-500 mb-6 leading-relaxed">
        הוסף מפתח גישה ותתחבר בפעם הבאה עם טביעת אצבע או זיהוי פנים — מהיר יותר, ומוגן מפני אתרי התחזות.
      </p>

      {error && <div className="mb-3 text-xs text-red-600">{error}</div>}

      <Button onClick={enroll} disabled={busy} className="w-full h-11">
        <Icon
          name={busy ? 'progress_activity' : 'fingerprint'}
          size={18}
          className={busy ? 'animate-spin' : ''}
        />
        {busy ? 'ממתין לאישור…' : 'הוסף מפתח גישה'}
      </Button>

      <button
        type="button"
        onClick={onSkip}
        disabled={busy}
        className="mt-3 w-full h-10 text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-60"
      >
        לא עכשיו
      </button>

      <div className="mt-4 text-[11px] text-slate-400">
        תמיד אפשר להוסיף או להסיר מפתחות גישה מעמוד הפרופיל.
      </div>
    </div>
  );
};

/** A human label for the credential, so the profile list is readable later. */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'המכשיר שלי';
}
